import { Roles, type AppRole } from "../types/permissions";
import { useMemo } from "react";

import { useAuthStore } from "../store/useAuthStore";
import type { UseAuthReturn } from "../types/auth.types";
import { resolvePortals } from "../utils/auth-navigation";

/**
 * Primary hook for consuming authentication state, user identity, and tenant permissions.
 * Derives active tenant role claims to support role-based authorization.
 */
export function useAuth(): UseAuthReturn {
  const session = useAuthStore((state) => state.session);
  const isInitialized = useAuthStore((state) => state.isInitialized);
  const clearSession = useAuthStore((state) => state.clearSession);
  const switchTenantAction = useAuthStore((state) => state.switchTenant);
  const requestScope = useAuthStore((state) => state.requestScope);
  const generation = useAuthStore((state) => state.generation);

  const activeTenant = useMemo(() => {
    if (!session) return null;
    return (
      session.user.tenants.find((t) => t.tenantId === session.activeTenantId) ??
      null
    );
  }, [session]);

  const roles = useMemo(() => {
    return activeTenant?.roles ?? [];
  }, [activeTenant]);

  const isAdmin = useMemo(() => roles.includes(Roles.Admin), [roles]);
  const isTeacher = useMemo(() => roles.includes(Roles.Teacher), [roles]);
  const isStudent = useMemo(() => roles.includes(Roles.Student), [roles]);

  // Counted over PORTALS, not raw roles: a role the client cannot route (an unknown server role)
  // must not push the user into a chooser that would then render a single option.
  const hasMultiplePortals = useMemo(
    () => resolvePortals(roles).length > 1,
    [roles],
  );

  return {
    requestScope,
    generation,
    isAuthenticated: Boolean(session?.accessToken),
    isInitialized,
    user: session?.user ?? null,
    accessToken: session?.accessToken ?? null,
    activeTenant,
    roles,
    isAdmin,
    isTeacher,
    isStudent,
    hasMultiplePortals,
    hasRole: (role: AppRole) => roles.includes(role),
    hasAnyRole: (candidateRoles: readonly AppRole[]) =>
      candidateRoles.some((role) => roles.includes(role)),
    switchTenant: switchTenantAction,
    logout: clearSession,
  };
}
